import { useCallback, useEffect, useMemo, useState } from 'react';

// Interfaccia base per tutti i nodi dell'albero
export interface BaseTreeNode {
    data: any;
    id: string;
    name: string;
    children?: BaseTreeNode[];
    /**
     * BLOCCATA: non si sceglie dal palco e non si trasforma — si sceglie solo da qui.
     *
     * Sta accanto a `visible` e non nel nodo di una specie sola, perché il lucchetto vale per
     * qualunque cosa la scena contenga: lo legge l'albero, che le disegna tutte.
     */
    locked?: boolean;
}

// Tipo generico per i nodi che estendono BaseTreeNode
export type TreeNodeWithOverlay<T extends BaseTreeNode> = T & {
    component?: () => React.ReactNode;
    visible?: boolean;
    disableOverlay?: boolean;
    onCustomAction?: (node: T) => void;
};

// Interfaccia per il tree state compatibile con TreeComponent
export interface TreeState<T extends BaseTreeNode> {
    // Trova il parent ID di un nodo
    addNode(newNode: T, parentId?: string | null): void;
    nodes: TreeNodeWithOverlay<T>[];
    openNodes: Set<string>;
    selectedIds: Set<string>;
    draggedId: string | null;
    dropTargetId: string | null;
    overlayNodeId: string | null;
    flatVisibleList: TreeNodeWithOverlay<T>[];
    setDraggedId: (id: string | null) => void;
    setDropTargetId: (id: string | null) => void;
    setOverlayNodeId: (id: string | null) => void;
    showOverlay: (id: string) => void;
    closeOverlay: () => void;
    findNodeById: (id: string) => TreeNodeWithOverlay<T> | undefined;
    findNodeByDataID: (nodes: any[], dataID: string) => TreeNodeWithOverlay<T> | undefined;
    findParentId: (id: string) => string | null;
    /** `order` dice su quale sequenza contare l'intervallo di ⇧: serve a chi mostra le stesse
     *  cose in un ordine suo — la griglia della libreria — dove l'ordine dell'albero mentirebbe. */
    toggleSelection: (id: string, shiftKey: boolean, ctrlKey: boolean, order?: string[]) => void;
    toggleNode: (id: string) => void;
    toggleVisibility: (id: string) => void;
    setVisibility: (id: string, visible: boolean) => void; // 🔧 NUOVO: Metodo per impostare direttamente la visibilità
    setSelectedIds: (ids: Set<string>) => void;
    moveSelection: (nodeIds: string[], targetParentId: string | null) => void;
    canTurnIntoFolder: (node: T) => boolean; // 🔧 NUOVO: Controlla se un nodo può diventare cartella
}

export interface TreeNodeHook<T extends BaseTreeNode> {
    nodes: TreeNodeWithOverlay<T>[];
    setNodes: React.Dispatch<React.SetStateAction<TreeNodeWithOverlay<T>[]>>;
    filter: string;
    setFilter: React.Dispatch<React.SetStateAction<string>>;
    selectedIds: string[];
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
    // Tree state object per TreeComponent
    treeState: TreeState<T>;

    // Computed
    treeData: TreeNodeWithOverlay<T>[];
    filteredData: TreeNodeWithOverlay<T>[];

    // Actions
    updateNode: (id: string, updates: Partial<T>) => void;
    findNodeById: (id: string) => TreeNodeWithOverlay<T> | undefined;
    removeNodes: (ids: string[]) => void;
    /**
     * IL SOGGETTO SI DICE, non si indovina. Prima queste due leggevano la selezione da dentro,
     * e finché i comandi stavano solo nella barra sopra l'elenco andava bene: la selezione era
     * l'unica cosa su cui potessero cadere. Da quando ogni riga ha il suo «…», il soggetto può
     * essere una riga che non è selezionata — e un motore che guarda altrove lavorerebbe sulla
     * cosa sbagliata. Come `removeNodes`, che l'elenco l'ha sempre ricevuto.
     */
    cloneNodes: (ids: string[], suffix?: string, getNode?: (node: T) => any) => T[] | undefined;
    groupNodes: (ids: string[], groupName: string, createNewNode: (name: string) => T, findParentId?: (nodeId: string) => string | null) => T | null;
    deepCloneNode: (node: T, suffix?: string) => T;
    addNode: (newNode: T, parentId?: string | null) => void;
    syncInitialVisibilityFromMeshes: () => void;
    // Handlers
    handleSelectedChange: (selectedIds: string[]) => void;
}

// Hook per la gestione generica dei nodi dell'albero
export function useTreeNodeManager<T extends BaseTreeNode>(initialNodes: T[], createOverlayComponent: (node: T) => () => React.ReactNode): TreeNodeHook<T> {
    const [nodes, setNodes] = useState<T[]>(initialNodes);
    const [filter, setFilter] = useState<string>('');
    const [selectedIdsArray, setSelectedIdsArray] = useState<string[]>([]);
    const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [dropTargetId, setDropTargetId] = useState<string | null>(null);
    const [overlayNodeId, setOverlayNodeId] = useState<string | null>(null);
    const [hiddenNodes, setHiddenNodes] = useState<Set<string>>(new Set());

    // 🆕 NUOVO: Mantieni traccia dell'ultimo elemento selezionato per la selezione Shift
    const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

    // 🔧 NUOVO: Metodo per sincronizzare la visibilità iniziale dalle mesh di Babylon.js
    const syncInitialVisibilityFromMeshes = useCallback(() => {
        console.log('🔄 Syncing initial visibility from Babylon.js meshes...');

        const hiddenNodeIds = new Set<string>();

        const checkNodeVisibility = (nodeList: T[], parentHidden: boolean = false) => {
            nodeList.forEach((node) => {
                // Debug: stampa la struttura del nodo per capire come accedere alla mesh
                console.log(`🔍 Analyzing node: ${node.name} (${node.id})`, {
                    hasData: !!(node as any).data,
                    dataIsVisible: (node as any).data?.isVisible,
                    dataType: typeof (node as any).data,
                    isMesh: !!(node as any).data?.id && !!(node as any).data?.name, // Controlla se sembra una mesh
                    parentHidden
                });

                // 🔧 MIGLIORATO: Solo se il nodo è esplicitamente nascosto E il parent non è già nascosto
                //
                // E ANCHE IL CAMPO DEL NODO, non solo la mesh sotto. `data.isVisible` è una domanda che
                // ha senso per una mesh e per nient'altro: sotto una luce c'è una luce, che si spegne
                // con `isEnabled()`. Chi costruisce l'elenco quella risposta la scrive già in `visible`,
                // e senza guardarla una luce spenta nella scena nasceva accesa nella lista.
                const isNodeHidden = (node as any).data?.isVisible === false || (node as any).visible === false;

                if (isNodeHidden && !parentHidden) {
                    console.log(`🔍 Found explicitly hidden mesh: ${node.name} (${node.id}) - adding to hiddenNodes`);
                    hiddenNodeIds.add(node.id);
                } else if (isNodeHidden && parentHidden) {
                    console.log(`🔍 Node ${node.name} is hidden but parent is already hidden - skipping`);
                }

                // Controlla ricorsivamente i children
                // Passa parentHidden=true se questo nodo è nascosto OR se il parent era già nascosto
                if (node.children && node.children.length > 0) {
                    checkNodeVisibility(node.children as T[], isNodeHidden || parentHidden);
                }
            });
        };

        checkNodeVisibility(nodes);

        if (hiddenNodeIds.size > 0) {
            console.log(`🔧 Setting ${hiddenNodeIds.size} explicitly hidden nodes:`, Array.from(hiddenNodeIds));
            setHiddenNodes(hiddenNodeIds);
        } else {
            console.log('✅ No explicitly hidden meshes found during initial sync');
        }
    }, [nodes]);

    // L'ALBERO SI RIFÀ, NON CAMBIA — e la differenza è tutto.
    //
    // Questo effetto scatta ogni volta che chi ci ospita ricostruisce l'elenco, e lo ricostruisce
    // leggendo la scena: basta che il motore aggiunga o tolga una mesh di servizio — un contorno
    // di selezione, un aiuto di scena — perché l'elenco sia un ARRAY NUOVO con dentro le stesse
    // identiche cose. Azzerando tutto, ogni ricostruzione chiudeva il pannello aperto e cancellava
    // la selezione; e siccome la scena si rilegge a intervalli, il pannello si richiudeva da solo
    // subito dopo essersi aperto. Da fuori: «clicco una riga e non succede niente».
    //
    // Ora sopravvive ciò che esiste ancora, e se ne va solo ciò che è sparito davvero.
    //
    // LA RIGA APERTA FA ECCEZIONE, e non per distrazione. Una riga può sparire perché la si è
    // tolta, ma anche perché la si sta SOSTITUENDO: cambiare la specie di una luce vuol dire
    // rifarla, e per un istante la vecchia è morta e la nuova non è ancora nell'elenco. Da qui
    // dentro le due cose sono indistinguibili, e chiudere il pannello a metà sostituzione fa
    // allargare la scena 3D e subito dopo restringerla — due riflussi di tutta la pagina per un
    // gesto che l'utente vede come uno solo.
    //
    // Chiudere per davvero lo dice chi toglie, chiamando `closeOverlay`: è un gesto, non un
    // effetto collaterale. E chi disegna il pannello sa già cosa fare di una riga che non trova.
    useEffect(() => {
        setNodes(initialNodes);

        const alive = new Set<string>();
        const collect = (list: T[]) =>
            list.forEach((node) => {
                alive.add(node.id);
                if (node.children) collect(node.children as T[]);
            });
        collect(initialNodes ?? []);

        setSelectedIdsArray((current) => (current.every((id) => alive.has(id)) ? current : current.filter((id) => alive.has(id))));
        setLastSelectedId((current) => (current && alive.has(current) ? current : null));
    }, [initialNodes]);

    // Converte array in Set per compatibilità
    const selectedIds = useMemo(() => new Set(selectedIdsArray), [selectedIdsArray]);

    const addNode = useCallback((newNode: T, parentId?: string | null) => {
        setNodes((prev) => {
            if (!parentId) {
                // Aggiungi in root
                return [...prev, newNode];
            }

            // Aggiungi come child del parent specificato
            const addToParent = (nodeList: T[]): T[] =>
                nodeList.map((node) => {
                    if (node.id === parentId) {
                        return {
                            ...node,
                            children: [...(node.children || []), newNode]
                        };
                    }
                    if (node.children) {
                        return {
                            ...node,
                            children: addToParent(node.children as T[])
                        };
                    }
                    return node;
                });

            return addToParent(prev);
        });
    }, []);

    // Funzione generica per aggiornare un nodo
    const updateNode = useCallback((id: string, updates: Partial<T>) => {
        setNodes((prev) => {
            const updateRecursive = (nodeList: T[]): T[] =>
                nodeList.map((node) => {
                    if (node.id === id) {
                        return { ...node, ...updates };
                    }
                    if (node.children && node.children.length > 0) {
                        return { ...node, children: updateRecursive(node.children as T[]) };
                    }
                    return node;
                });

            return updateRecursive(prev);
        });
    }, []);

    // Funzione generica per trovare un nodo per ID
    const findNodeById = useCallback(
        (id: string, nodeList: T[] = nodes): T | undefined => {
            for (const node of nodeList) {
                if (node.id === id) return node;
                if (node.children) {
                    const found = findNodeById(id, node.children as T[]);
                    if (found) return found;
                }
            }
            return undefined;
        },
        [nodes]
    );

    function findNodeByDataID(nodes: TreeNodeWithOverlay<T>[], meshId: string): TreeNodeWithOverlay<T> | undefined {
        for (const node of nodes) {
            // Controllo su questo nodo
            // L'IDENTITÀ DEL NODO NEL MONDO 3D, non solo la sua mesh. Un nodo dell'albero dei
            // materiali porta il materiale in `data.material`, uno delle luci la luce in
            // `data.light`: cercandoli per `data.id` non si trovavano, e chi chiedeva di ritrovare
            // un materiale finiva su un elenco senza niente di acceso.
            const data = (node as any).data;
            if (data?.id === meshId || data?.uuid === meshId || data?.material?.id === meshId || data?.light?.id === meshId) {
                return node;
            }
            // Se ha figli, cerco in profondità
            if (node.children) {
                const found = findNodeByDataID(node.children as TreeNodeWithOverlay<T>[], meshId);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    }

    // Trova il parent ID di un nodo
    const findParentIdInNodes = useCallback(
        (nodeId: string, nodeList: T[] = nodes, parentId: string | null = null): string | null => {
            for (const node of nodeList) {
                if (node.id === nodeId) return parentId;
                if (node.children) {
                    const found = findParentIdInNodes(nodeId, node.children as T[], node.id);
                    if (found !== null) return found;
                }
            }
            return null;
        },
        [nodes]
    );

    // Verifica se un nodo è antenato di un altro
    const isAncestor = useCallback(
        (ancestorId: string, nodeId: string): boolean => {
            const node = findNodeById(nodeId);
            if (!node) return false;

            const checkAncestry = (currentNode: T): boolean => {
                if (!currentNode.children) return false;

                for (const child of currentNode.children as T[]) {
                    if (child.id === ancestorId) return true;
                    if (checkAncestry(child)) return true;
                }
                return false;
            };

            return checkAncestry(node);
        },
        [findNodeById]
    );

    // Rimuove un singolo nodo (con tutti i suoi figli) dall'albero
    const removeNodeById = useCallback((list: T[], id: string): { newList: T[]; removed: T | null } => {
        let removed: T | null = null;
        const recurse = (nodes: T[]): T[] =>
            nodes
                .filter((n) => {
                    if (n.id === id) {
                        removed = n;
                        return false;
                    }
                    return true;
                })
                .map((n) => ({
                    ...n,
                    children: n.children ? recurse(n.children as T[]) : []
                }));
        return { newList: recurse(list), removed };
    }, []);

    // Inserisce `node` dentro `parentId` (o in root se parentId è null) all'indice `index`
    const insertNodeAt = useCallback((list: T[], node: T, parentId: string | null, index: number): T[] => {
        if (parentId === null) {
            const copy = [...list];
            copy.splice(index, 0, node);
            return copy;
        }
        const recurse = (nodes: T[]): T[] =>
            nodes.map((n) => {
                if (n.id === parentId) {
                    const children = [...(n.children ?? [])];
                    children.splice(index, 0, node);
                    return { ...n, children };
                }
                return {
                    ...n,
                    children: n.children ? recurse(n.children as T[]) : []
                };
            });
        return recurse(list);
    }, []);

    // Converte un nodo folder vuoto in item normale (rimuove children vuoti)
    const normalizeEmptyFolders = useCallback((nodeList: T[]): T[] => {
        return nodeList.map((node) => {
            if (node.children && node.children.length === 0) {
                // Rimuovi la proprietà children se è vuota
                const { children, ...nodeWithoutChildren } = node;
                return nodeWithoutChildren as T;
            } else if (node.children && node.children.length > 0) {
                // Ricorsivamente normalizza i children
                return {
                    ...node,
                    children: normalizeEmptyFolders(node.children as T[])
                };
            }
            return node;
        });
    }, []);

    // moveSelection: sposta una lista di id come figli di targetId (o in root se targetId è null)
    const moveSelection = useCallback(
        (selIds: string[], targetId: string | null) => {
            // Verifica che non stiamo spostando un nodo su se stesso o su un suo discendente
            if (targetId && selIds.includes(targetId)) {
                console.warn('Cannot move node to itself');
                return;
            }

            // Verifica che non stiamo spostando un nodo su un suo discendente
            if (targetId) {
                for (const selId of selIds) {
                    if (isAncestor(targetId, selId)) {
                        console.warn('Cannot move node to its descendant');
                        return;
                    }
                }
            }

            // 🔧 NUOVO: Determina se stiamo facendo un riordino interno al gruppo
            // Se tutti i nodi selezionati hanno lo stesso parent del target, è un riordino interno
            const isInternalReorder =
                targetId &&
                selIds.every((selId) => {
                    const selParent = findParentIdInNodes(selId);
                    const targetParent = findParentIdInNodes(targetId);
                    return selParent === targetParent && selParent !== null;
                });

            // 🔧 NUOVO: Salva l'ID del gruppo parent se è un riordino interno
            let parentGroupId: string | null = null;
            if (isInternalReorder && targetId) {
                parentGroupId = findParentIdInNodes(targetId);
            }

            setNodes((prev) => {
                let tree = prev;
                const removedNodes: T[] = [];

                // 1) Rimuovi tutti i nodi selezionati
                selIds.forEach((id) => {
                    const { newList, removed } = removeNodeById(tree, id);
                    tree = newList;
                    if (removed) {
                        removedNodes.push(removed);
                    }
                });

                // 2) Inserisci tutti i nodi rimossi nel target
                removedNodes.forEach((node, index) => {
                    const insertIndex = targetId ? (findNodeById(targetId, tree)?.children?.length ?? 0) + index : tree.length;
                    tree = insertNodeAt(tree, node, targetId, insertIndex);
                });

                // 3) Normalizza le cartelle vuote
                tree = normalizeEmptyFolders(tree);

                return tree;
            });

            // 🔧 NUOVO: Se è un riordino interno, assicurati che il gruppo parent rimanga aperto
            if (isInternalReorder && parentGroupId) {
                setOpenNodes((prev) => {
                    const newSet = new Set(prev);
                    newSet.add(parentGroupId!);
                    return newSet;
                });
            }

            // Aggiorna la selezione per riflettere i nodi spostati
            setSelectedIdsArray(selIds);
        },
        [removeNodeById, insertNodeAt, findNodeById, findNodeByDataID, isAncestor, normalizeEmptyFolders, findParentIdInNodes]
    );

    // Espongo moveSelection anche come moveNode per compatibilità single-node
    const moveNode = useCallback(
        (nodeId: string, parentId: string | null, index: number) => {
            moveSelection([nodeId], parentId);
        },
        [moveSelection]
    );

    const removeNodesFromTree = <N extends BaseTreeNode>(nodes: N[], idsToRemove: string[]): N[] => {
        return nodes
            .filter((node) => !idsToRemove.includes(node.id))
            .map((node) => ({
                ...node,
                children: node.children ? removeNodesFromTree(node.children as N[], idsToRemove) : []
            }));
    };

    // Funzione generica per rimuovere nodi
    const removeNodes = useCallback(
        (idsToRemove: string[]) => {
            setNodes((prev) => {
                const removeRecursive = (nodeList: T[]): T[] =>
                    nodeList
                        .filter((node) => !idsToRemove.includes(node.id))
                        .map((node) => ({
                            ...node,
                            children: node.children ? removeRecursive(node.children as T[]) : []
                        }));
                return normalizeEmptyFolders(removeRecursive(prev));
            });
        },
        [normalizeEmptyFolders]
    );

    // Funzione generica per il deep clone
    const deepCloneNode = useCallback((node: T, suffix: string = 'Copy'): T => {
        const cloned = {
            ...node,
            id: `${node.id}-clone-${Date.now()}`,
            name: `${node.name} ${suffix}`,
            children: node.children ? node.children.map((child) => deepCloneNode(child as T, suffix)) : []
        } as T;
        return cloned;
    }, []);

    // Funzione per inserire cloni dopo gli originali
    const insertClonesAfterOriginals = useCallback((nodeList: T[], selectedIds: string[], clones: T[]): T[] => {
        const result: T[] = [];

        for (const node of nodeList) {
            result.push(node);

            // Se questo nodo è selezionato, aggiungi il suo clone subito dopo
            const selectedIndex = selectedIds.indexOf(node.id);
            if (selectedIndex !== -1) {
                result.push(clones[selectedIndex]);
            }

            // Gestisci ricorsivamente i children
            if (node.children && node.children.length > 0) {
                const updatedNode = {
                    ...node,
                    children: insertClonesAfterOriginals(node.children as T[], selectedIds, clones)
                } as T;
                result[result.length - (selectedIndex !== -1 ? 2 : 1)] = updatedNode;
            }
        }

        return result;
    }, []);

    // Funzione per raggruppare nodi sotto un parent
    const groupNodesUnderParent = useCallback((originalNodes: T[], selectedIds: string[], newGroup: T, parentId: string | null): T[] => {
        // Raccoglie i nodi selezionati
        const movers: T[] = [];

        // Rimuove i selectedIds dall'albero
        const filterOut = (nodeList: T[]): T[] => {
            return nodeList.reduce<T[]>((acc, node) => {
                if (selectedIds.includes(node.id)) {
                    movers.push(node);
                    return acc;
                }
                const filteredChildren = node.children?.length ? filterOut(node.children as T[]) : [];
                acc.push({ ...node, children: filteredChildren } as T);
                return acc;
            }, []);
        };

        // Filtra via i nodi da spostare
        const pruned = filterOut(originalNodes);

        // Assegna i figli al gruppo
        (newGroup as any).children = movers;

        // Se parentId === null, lo metto in radice
        if (parentId === null) {
            return [...pruned, newGroup];
        }

        // Altrimenti lo inserisco ricorsivamente come child di parentId
        const insertRecursive = (nodeList: T[]): T[] => {
            return nodeList.map((node) => {
                if (node.id === parentId) {
                    return {
                        ...node,
                        children: [...(node.children || []), newGroup]
                    } as T;
                }
                if (node.children && node.children.length) {
                    return {
                        ...node,
                        children: insertRecursive(node.children as T[])
                    } as T;
                }
                return node;
            });
        };

        return insertRecursive(pruned);
    }, []);

    // Funzione generica per clonare nodi selezionati
    type CloneDataFunction<T> = (node: T) => any;

    const cloneNodes = useCallback(
        (ids: string[], suffix: string = 'Copy', customCloneDataProperty?: CloneDataFunction<T>) => {
            if (!ids.length) return;

            const nodesToClone: T[] = [];
            ids.forEach((id) => {
                const node = findNodeById(id);
                if (node) {
                    nodesToClone.push(node);
                }
            });

            if (nodesToClone.length === 0) return;

            const clones = nodesToClone.map((node) => {
                const clonedNode = deepCloneNode(node, suffix);

                if (customCloneDataProperty) {
                    const assignClonedDataRecursively = (n: T) => {
                        n.data = customCloneDataProperty(n);
                        if (Array.isArray(n.children)) {
                            (n.children as T[]).forEach(assignClonedDataRecursively);
                        }
                    };
                    assignClonedDataRecursively(clonedNode);
                }

                return clonedNode;
            });

            setNodes((prev) => insertClonesAfterOriginals(prev, ids, clones));
            const newIds = clones.map((clone) => clone.id);
            setSelectedIdsArray(newIds);

            return clones;
        },
        [findNodeById, deepCloneNode, insertClonesAfterOriginals]
    );

    // Funzione per raggruppare nodi selezionati
    const groupNodes = useCallback(
        (ids: string[], groupName: string, createNewNode: (name: string) => T, findParentId?: (nodeId: string) => string | null) => {
            if (!ids.length) return null;

            // Crea il template del gruppo
            const newGroup = createNewNode(groupName);
            (newGroup as any).children = [];

            // Individua il parent comune
            const parentId = ids.length === 1 && findParentId ? findParentId(ids[0]) || null : null;

            // Aggiorna lo stato
            setNodes((prev) => groupNodesUnderParent(prev, ids, newGroup, parentId));

            return newGroup;
        },
        [groupNodesUnderParent]
    );

    // Mappa i dati per il TreeComponent aggiungendo il componente overlay
    const treeData = useMemo(() => {
        // 🔧 MIGLIORATO: Calcola la visibilità in modo gerarchico
        const mapNodes = (nodeList: T[], parentPath: string[] = []): TreeNodeWithOverlay<T>[] =>
            nodeList.map((node) => {
                const currentPath = [...parentPath, node.id];

                // Un nodo è visibile se:
                // 1. Non è esplicitamente nascosto
                // 2. Nessuno dei suoi parent è nascosto
                const isExplicitlyHidden = hiddenNodes.has(node.id);
                const hasHiddenParent = parentPath.some((parentId) => hiddenNodes.has(parentId));
                const isVisible = !isExplicitlyHidden && !hasHiddenParent;

                return {
                    ...node,
                    component: createOverlayComponent(node),
                    visible: isVisible,
                    children: node.children ? mapNodes(node.children as T[], currentPath) : []
                };
            });

        return mapNodes(nodes);
    }, [nodes, hiddenNodes, createOverlayComponent]); // Funzione per creare una lista piatta dei nodi visibili
    const createFlatVisibleList = useCallback(
        (nodeList: TreeNodeWithOverlay<T>[]): TreeNodeWithOverlay<T>[] => {
            const flatList: TreeNodeWithOverlay<T>[] = [];

            const traverse = (nodes: TreeNodeWithOverlay<T>[], depth: number = 0) => {
                nodes.forEach((node) => {
                    flatList.push({ ...node, depth } as any);
                    if (node.children && openNodes.has(node.id)) {
                        traverse(node.children as TreeNodeWithOverlay<T>[], depth + 1);
                    }
                });
            };

            traverse(nodeList);
            return flatList;
        },
        [openNodes]
    );

    // Filtra i dati per la ricerca
    const filteredData = useMemo(() => {
        if (!filter) return treeData;
        const lower = filter.toLowerCase();
        const match = (node: TreeNodeWithOverlay<T>) => node.name.toLowerCase().includes(lower);

        const traverse = (list: TreeNodeWithOverlay<T>[]): TreeNodeWithOverlay<T>[] => {
            const result: TreeNodeWithOverlay<T>[] = [];

            for (const n of list) {
                // Se il nodo corrisponde al filtro, includi TUTTI i suoi figli
                if (match(n)) {
                    result.push({
                        ...n,
                        children: (n.children as TreeNodeWithOverlay<T>[]) || [] // Mantieni tutti i figli
                    });
                } else {
                    // Se il nodo non corrisponde, controlla i figli
                    const filteredChildren = n.children ? traverse(n.children as TreeNodeWithOverlay<T>[]) : [];
                    // Se ha figli filtrati, includi questo nodo come parent
                    if (filteredChildren.length > 0) {
                        result.push({
                            ...n,
                            children: filteredChildren
                        });
                    }
                }
            }

            return result;
        };

        return traverse(treeData);
    }, [treeData, filter]);

    // Lista piatta dei nodi visibili
    //
    // MENTRE SI CERCA SONO VISIBILI TUTTE: l’albero apre i rami da sé quando un filtro è attivo, e
    // questa lista è ciò che «Select all» prende. Lasciandola alle sole righe aperte, prendere tutto
    // avrebbe preso meno di quello che si vede.
    const flatVisibleList = useMemo(() => {
        if (filter) {
            const all: TreeNodeWithOverlay<T>[] = [];
            const collect = (list: TreeNodeWithOverlay<T>[], depth = 0) => {
                list.forEach((node) => {
                    all.push({ ...node, depth } as any);
                    if (node.children) collect(node.children as TreeNodeWithOverlay<T>[], depth + 1);
                });
            };
            collect(filteredData);
            return all;
        }

        return createFlatVisibleList(filteredData);
    }, [filteredData, createFlatVisibleList, filter]);

    // Funzioni per il TreeState
    const toggleSelection = useCallback(
        (id: string, shiftKey: boolean, ctrlKey: boolean, order?: string[]) => {
            setSelectedIdsArray((prev) => {
                const newIds = new Set(prev);

                if (shiftKey && lastSelectedId && lastSelectedId !== id) {
                    // 🆕 SELEZIONE CON SHIFT: Seleziona tutti gli elementi tra l'ultimo selezionato e il corrente
                    const flatIds = order ?? flatVisibleList.map((node) => node.id);
                    const lastIndex = flatIds.indexOf(lastSelectedId);
                    const currentIndex = flatIds.indexOf(id);

                    if (lastIndex !== -1 && currentIndex !== -1) {
                        // Determina l'intervallo da selezionare
                        const startIndex = Math.min(lastIndex, currentIndex);
                        const endIndex = Math.max(lastIndex, currentIndex);

                        // Se non c'è Ctrl, parte da una selezione pulita
                        if (!ctrlKey) {
                            newIds.clear();
                        }

                        // Seleziona tutti gli elementi nell'intervallo
                        for (let i = startIndex; i <= endIndex; i++) {
                            newIds.add(flatIds[i]);
                        }

                        // Non aggiornare lastSelectedId per permettere selezioni consecutive con Shift
                        return Array.from(newIds);
                    }
                }

                if (ctrlKey) {
                    // SELEZIONE CON CTRL: Toggle del singolo elemento
                    if (newIds.has(id)) {
                        newIds.delete(id);
                    } else {
                        newIds.add(id);
                    }
                } else if (!shiftKey) {
                    // SELEZIONE NORMALE: Solo l'elemento corrente
                    newIds.clear();
                    newIds.add(id);
                }

                // Aggiorna l'ultimo elemento selezionato solo per selezioni normali o Ctrl
                if (!shiftKey) {
                    setLastSelectedId(id);
                }

                return Array.from(newIds);
            });
        },
        [lastSelectedId, flatVisibleList]
    );

    const toggleNode = useCallback(
        (id: string) => {
            // 🔧 NUOVO: Previeni la chiusura di nodi che sono parent di elementi in drag
            // Questo risolve il problema del gruppo che si chiude durante il drag interno
            if (draggedId) {
                const draggedParent = findParentIdInNodes(draggedId);
                if (draggedParent === id) {
                    console.log('🚫 Prevented closing parent group during drag:', id);
                    return; // Non chiudere il parent del nodo in drag
                }
            }

            setOpenNodes((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(id)) {
                    newSet.delete(id);
                } else {
                    newSet.add(id);
                }
                return newSet;
            });
        },
        [draggedId, findParentIdInNodes]
    );

    const toggleVisibility = useCallback((id: string) => {
        setHiddenNodes((prev) => {
            const next = new Set(prev);
            let isVisible: boolean;
            if (next.has(id)) {
                next.delete(id);
                isVisible = true;
            } else {
                next.add(id);
                isVisible = false;
            }
            // Aggiorna la proprietà visible del nodo stesso
            setNodes((prevNodes) => {
                const updateVisibility = (nodes: T[]): T[] =>
                    nodes.map((node) => {
                        if (node.id === id) {
                            return { ...node, visible: isVisible };
                        }
                        if (node.children) {
                            return { ...node, children: updateVisibility(node.children as T[]) };
                        }
                        return node;
                    });
                return updateVisibility(prevNodes);
            });
            return next;
        });
    }, []);

    // 🔧 NUOVO: Metodo per impostare direttamente la visibilità senza toggle
    const setVisibility = useCallback((id: string, visible: boolean) => {
        setHiddenNodes((prev) => {
            const next = new Set(prev);
            if (visible) {
                // Se deve essere visibile, rimuovi dalla lista dei nascosti
                next.delete(id);
            } else {
                // Se deve essere nascosto, aggiungi alla lista dei nascosti
                next.add(id);
            }

            // Aggiorna la proprietà visible del nodo stesso
            setNodes((prevNodes) => {
                const updateVisibility = (nodes: T[]): T[] =>
                    nodes.map((node) => {
                        if (node.id === id) {
                            return { ...node, visible };
                        }
                        if (node.children) {
                            return { ...node, children: updateVisibility(node.children as T[]) };
                        }
                        return node;
                    });
                return updateVisibility(prevNodes);
            });
            return next;
        });
    }, []);

    const setSelectedIdsSet = useCallback((ids: Set<string>) => {
        setSelectedIdsArray(Array.from(ids));

        // 🆕 Aggiorna lastSelectedId quando la selezione viene modificata programmaticamente
        // Prendi l'ultimo elemento della selezione come nuovo lastSelectedId
        const idsArray = Array.from(ids);
        if (idsArray.length > 0) {
            setLastSelectedId(idsArray[idsArray.length - 1]);
        } else {
            setLastSelectedId(null);
        }
    }, []);

    const findNodeByIdInTree = useCallback(
        (id: string): TreeNodeWithOverlay<T> | undefined => {
            const search = (nodeList: TreeNodeWithOverlay<T>[]): TreeNodeWithOverlay<T> | undefined => {
                for (const node of nodeList) {
                    if (node.id === id) return node;
                    if (node.children) {
                        const found = search(node.children as TreeNodeWithOverlay<T>[]);
                        if (found) return found;
                    }
                }
                return undefined;
            };
            return search(treeData);
        },
        [treeData]
    );

    const findParentIdInTree = useCallback(
        (nodeId: string): string | null => {
            const search = (nodeList: TreeNodeWithOverlay<T>[], parentId: string | null = null): string | null => {
                for (const node of nodeList) {
                    if (node.id === nodeId) return parentId;
                    if (node.children) {
                        const found = search(node.children as TreeNodeWithOverlay<T>[], node.id);
                        if (found !== null) return found;
                    }
                }
                return null;
            };
            return search(treeData);
        },
        [treeData]
    );

    // Metodi per gestire l'overlay
    const showOverlay = useCallback((id: string) => {
        setOverlayNodeId(id);
    }, []);

    const closeOverlay = useCallback(() => {
        setOverlayNodeId(null);
    }, []);

    // Determina se un nodo può diventare cartella
    const canTurnIntoFolder = useCallback((node: T) => !(node as any).notTurnableIntoFolder, []);

    // Tree state object compatibile con TreeComponent
    const treeState: TreeState<T> = useMemo(
        () => ({
            nodes: treeData,
            openNodes,
            selectedIds,
            draggedId,
            dropTargetId,
            overlayNodeId,
            flatVisibleList,
            setDraggedId,
            setDropTargetId,
            setOverlayNodeId,
            showOverlay,
            closeOverlay,
            findNodeById: findNodeByIdInTree,
            findParentId: findParentIdInTree,
            findNodeByDataID,
            toggleSelection,
            toggleNode,
            toggleVisibility,
            setVisibility, // 🔧 NUOVO: Aggiunto il metodo setVisibility
            setSelectedIds: setSelectedIdsSet,
            moveNode,
            moveSelection,
            addNode,
            canTurnIntoFolder // 🔧 NUOVO: Aggiunto il controllo per folder
        }),
        [
            treeData,
            openNodes,
            selectedIds,
            draggedId,
            dropTargetId,
            overlayNodeId,
            flatVisibleList,
            findNodeByIdInTree,
            findNodeByDataID,
            findParentIdInTree,
            toggleSelection,
            toggleNode,
            toggleVisibility,
            setVisibility, // 🔧 NUOVO: Aggiunto alla lista delle dipendenze
            setSelectedIdsSet,
            moveNode,
            moveSelection,
            showOverlay,
            closeOverlay,
            canTurnIntoFolder // 🔧 NUOVO: Aggiunto alla lista delle dipendenze
        ]
    );

    return {
        // State
        nodes,
        setNodes,
        filter,
        setFilter,
        selectedIds: selectedIdsArray,
        setSelectedIds: setSelectedIdsArray,

        // Tree state object per TreeComponent
        treeState,

        // Computed
        treeData,
        filteredData,

        // Actions
        updateNode,
        findNodeById,
        removeNodes,
        cloneNodes,
        groupNodes,
        deepCloneNode,
        addNode,
        syncInitialVisibilityFromMeshes,
        // Handlers
        handleSelectedChange: setSelectedIdsArray
    };
}
